export type ExportSectionDefaults = {
  application: boolean;
  contact: boolean;
  sow: boolean;
  invoice: boolean;
};

export type TaxDropdownOption = { value: string; label: string };

export type SingleTaxConfig = {
  type: 'single';
  label: string;
  options: TaxDropdownOption[];
};

export type SplitTaxConfig = {
  type: 'split';
  gst: { label: string; options: TaxDropdownOption[] };
  pst: { label: string; options: TaxDropdownOption[] };
};

export type TaxConfig = SingleTaxConfig | SplitTaxConfig;

// tempAuditor is intentionally absent from every region's invitableRoles:
// auditor invitations are superAdmin-only (gated in NewUser + server-side),
// not a per-region partnerAdmin capability.
export type InvitableRole = 'tempAdmin' | 'tempExpert' | 'tempProvider' | 'tempGrowthCoach' | 'tempAuditor';

const ALL_ROLES: InvitableRole[] = ['tempAdmin', 'tempExpert', 'tempProvider', 'tempGrowthCoach'];
// NF's program model has no Growth Coach step — no GC invites either.
// Derived (not hand-copied) so a future role added to ALL_ROLES can't silently miss NF.
const NF_ROLES: InvitableRole[] = ALL_ROLES.filter((r) => r !== "tempGrowthCoach");
const MV_ROLES: InvitableRole[] = ['tempAdmin'];
// Growth-Coach-flow email templates — hidden for regions whose program model has no GC step
const GC_TEMPLATE_KEYS = ["GC_INVITE", "GC_APPLICATION_UPDATE"];

export type RegionCapabilities = {
  hasFullPortal: boolean;
  // Whether the region's program model includes Growth Coach assessments
  usesGrowthCoaches: boolean;
  canInviteAllRoles: boolean;
  invitableRoles: InvitableRole[];
  canSyncPowerAutomate: boolean;
  canManageFirms: boolean;
  firmSourceRegions: string[];
  visibleNavItems: string[];
  defaultExportSections: ExportSectionDefaults;
  taxConfig: TaxConfig;
  allowedEmailTypes: string[];
  excludedTemplateKeys?: string[];
};

const ALL_EMAIL_TYPES = [
  'verification', 'password_reset', 'invitation', 'application_status',
  'expert_invitation', 'notification', 'terms_conditions', 'rejection',
  'sow_update', 'invoice', 'custom', 'system',
];

const MV_EMAIL_TYPES = [
  'verification', 'password_reset', 'invitation', 'application_status',
  'notification', 'terms_conditions', 'custom', 'system',
];

const MV_EXCLUDED_TEMPLATE_KEYS = [
  'GC_INVITE',
  'GC_APPLICATION_UPDATE',
  'SPF_ONBOARD_REQUEST',
  'IPE_REASSIGNMENT',
  'IPE_ASSIGNMENT',
  'SOW_KICKOFF',
  'SP_CONFIRM_REQUEST',
];

const FULL_PORTAL_NAV = [
  'Dashboard',
  'Applications',
  'Statements of Work',
  'Invoices',
  'Startups',
  'Service Providers',
  'Team & Invitations',
  'Email Management',
  'Audits',
  'Settings',
];

const MV_NAV = [
  'Dashboard',
  'Applications',
  'Service Providers',
  'Team & Invitations',
  'Email Management',
  'Audits',
  'Settings',
];

export const REGION_CAPABILITIES: Record<string, RegionCapabilities> = {
  communitech: {
    hasFullPortal: true,
    usesGrowthCoaches: true,
    canInviteAllRoles: true,
    invitableRoles: ALL_ROLES,
    canSyncPowerAutomate: false,
    canManageFirms: true,
    firmSourceRegions: ['communitech'],
    visibleNavItems: FULL_PORTAL_NAV,
    defaultExportSections: { application: true, contact: true, sow: true, invoice: true },
    allowedEmailTypes: ALL_EMAIL_TYPES,
    taxConfig: {
      type: 'single',
      label: 'Tax',
      options: [
        { value: '0', label: '0%' },
        { value: '0.05', label: '5%' },
        { value: '0.13', label: '13%' },
      ],
    },
  },
  investOttawa: {
    hasFullPortal: true,
    usesGrowthCoaches: true,
    canInviteAllRoles: true,
    invitableRoles: ALL_ROLES,
    canSyncPowerAutomate: true,
    canManageFirms: true,
    firmSourceRegions: ['investOttawa'],
    visibleNavItems: FULL_PORTAL_NAV,
    defaultExportSections: { application: true, contact: true, sow: true, invoice: true },
    allowedEmailTypes: ALL_EMAIL_TYPES,
    taxConfig: {
      type: 'single',
      label: 'Tax',
      options: [
        { value: '0', label: '0%' },
        { value: '0.05', label: '5%' },
        { value: '0.13', label: '13%' },
      ],
    },
  },
  launchLab: {
    hasFullPortal: false,
    usesGrowthCoaches: true,
    canInviteAllRoles: false,
    invitableRoles: MV_ROLES,
    canSyncPowerAutomate: false,
    canManageFirms: false,
    firmSourceRegions: ['investOttawa', 'launchLab'],
    visibleNavItems: ['Dashboard', 'Applications', 'Service Providers', 'Team & Invitations', 'Settings'],
    defaultExportSections: { application: true, contact: true, sow: false, invoice: false },
    allowedEmailTypes: MV_EMAIL_TYPES,
    excludedTemplateKeys: MV_EXCLUDED_TEMPLATE_KEYS,
    taxConfig: {
      type: 'single',
      label: 'Tax',
      options: [
        { value: '0', label: '0%' },
        { value: '0.05', label: '5%' },
        { value: '0.13', label: '13%' },
      ],
    },
  },
  sparkCenter: {
    hasFullPortal: false,
    usesGrowthCoaches: true,
    canInviteAllRoles: false,
    invitableRoles: MV_ROLES,
    canSyncPowerAutomate: false,
    canManageFirms: false,
    firmSourceRegions: ['investOttawa', 'sparkCenter'],
    visibleNavItems: ['Dashboard', 'Applications', 'Service Providers', 'Team & Invitations', 'Settings'],
    defaultExportSections: { application: true, contact: true, sow: false, invoice: false },
    allowedEmailTypes: MV_EMAIL_TYPES,
    excludedTemplateKeys: MV_EXCLUDED_TEMPLATE_KEYS,
    taxConfig: {
      type: 'single',
      label: 'Tax',
      options: [
        { value: '0', label: '0%' },
        { value: '0.05', label: '5%' },
        { value: '0.13', label: '13%' },
      ],
    },
  },
  northForge: {
    hasFullPortal: true,
    usesGrowthCoaches: false,
    canInviteAllRoles: false,
    invitableRoles: NF_ROLES,
    canSyncPowerAutomate: false,
    canManageFirms: true,
    firmSourceRegions: ['northForge'],
    visibleNavItems: FULL_PORTAL_NAV,
    defaultExportSections: { application: true, contact: true, sow: true, invoice: true },
    allowedEmailTypes: ALL_EMAIL_TYPES,
    excludedTemplateKeys: GC_TEMPLATE_KEYS,
    taxConfig: {
      type: 'split',
      gst: {
        label: 'GST',
        options: [
          { value: '0', label: '0%' },
          { value: '0.05', label: '5%' },
        ],
      },
      pst: {
        label: 'PST',
        options: [
          { value: '0', label: '0%' },
          { value: '0.06', label: '6%' },
          { value: '0.07', label: '7%' },
          { value: '0.08', label: '8%' },
        ],
      },
    },
  },
};

export function getRegionSupportEmail(region: string | null | undefined): string {
  switch (region) {
    case 'northForge': return 'info@northforge.ca';
    case 'investOttawa': return 'ip@investottawa.ca';
    case 'launchLab': return 'ip@investottawa.ca'; // TODO: update when LL email confirmed
    case 'sparkCenter': return 'ip@investottawa.ca'; // TODO: update when SC email confirmed
    default: return 'elevateip@communitech.ca';
  }
}

/**
 * Get capabilities for a region, falling back to restrictive defaults if region is unknown.
 */
export function getRegionCapabilities(region: string | null | undefined): RegionCapabilities {
  if (!region) return REGION_CAPABILITIES.communitech;
  return REGION_CAPABILITIES[region] || {
    hasFullPortal: false,
    usesGrowthCoaches: false,
    canInviteAllRoles: false,
    invitableRoles: ['tempAdmin'] as InvitableRole[],
    canSyncPowerAutomate: false,
    canManageFirms: false,
    firmSourceRegions: [],
    visibleNavItems: ['Dashboard'],
    defaultExportSections: { application: false, contact: false, sow: false, invoice: false },
    allowedEmailTypes: MV_EMAIL_TYPES,
    excludedTemplateKeys: MV_EXCLUDED_TEMPLATE_KEYS,
    taxConfig: REGION_CAPABILITIES.communitech.taxConfig,
  };
}

/**
 * Get effective capabilities, merging static region config with dynamic DB overrides.
 * Used when fullPortalEnabled toggle affects IO's portal mode.
 */
export function getEffectiveCapabilities(
  region: string | null | undefined,
  fullPortalEnabled: boolean
): RegionCapabilities {
  const base = getRegionCapabilities(region);

  // Only IO is dynamically toggleable — all other regions use static config
  if (region !== 'investOttawa') return base;

  // If full portal is enabled, use the static (full) config as-is
  if (fullPortalEnabled) return base;

  // MV mode: override portal-related capabilities
  return {
    ...base,
    hasFullPortal: false,
    canInviteAllRoles: false,
    canManageFirms: true,
    firmSourceRegions: ['investOttawa'],
    invitableRoles: MV_ROLES,
    visibleNavItems: MV_NAV,
    defaultExportSections: { application: true, contact: true, sow: false, invoice: false },
    allowedEmailTypes: MV_EMAIL_TYPES,
    excludedTemplateKeys: MV_EXCLUDED_TEMPLATE_KEYS,
  };
}
